import CloseIcon from '@mui/icons-material/Close'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  InputBase,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { MidasConfig } from 'api/midas'
import theme from 'utils/theme'
import MessageBubble, { IncomingBubble, incomingSx } from './MessageBubble'
import { LAUNCHER_LABEL } from './MidasLauncher'
import MarkdownLite from './MarkdownLite'
import { useMidasChat } from './useMidasChat'

export const textButtonSx = {
  height: 'auto',
  minWidth: 0,
  fontSize: '0.875rem',
  textTransform: 'none',
  '&.Mui-disabled': { bgcolor: 'transparent', color: 'text.disabled' }
} as const

// Cam, TestFlight 2026-09-17: set the expectation (1 to 2 working days) and keep
// Midas in play. Tone of voice v1 (24 Sep): short sentences with full stops, no
// dashes, no emoji.
export const HANDED_OVER_BANNER =
  'The Goldie team has this now. They usually reply within 1 to 2 working days, here and by email. Want to keep chatting with Midas in the meantime? Just ask.'
export const COMPOSER_PLACEHOLDER = 'Type your question'

export const TALK_TO_A_PERSON = 'Talk to a person'
const TITLE_ID = 'midas-panel-title'

interface MidasPanelProps {
  open: boolean
  onClose: () => void
  config: MidasConfig
}

/** Within this many pixels of the end still counts as "at the bottom". */
const BOTTOM_SLACK_PX = 80

export default function MidasPanel({
  open,
  onClose,
  config
}: Readonly<MidasPanelProps>) {
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const chat = useMidasChat(open)
  const [draft, setDraft] = useState('')
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const hasCustomerMessage = chat.messages.some(
    m => m.author_type === 'customer'
  )
  const ready = !!chat.conversation && !chat.error && !chat.closed
  // Before the first message the quick replies usually carry this action.
  const showHandoverAction =
    hasCustomerMessage || !config.quick_replies.includes(TALK_TO_A_PERSON)
  const lastMessage = chat.messages[chat.messages.length - 1]

  // Keep the newest message in view as it grows — and when the panel itself resizes. On a
  // phone the on-screen keyboard shrinks the visual viewport, which used to leave the newest
  // message above the fold with the customer having to scroll down again (Greg, 18 Sep).
  // Someone scrolled up reading history is left where they are.
  const atBottom = useRef(true)

  const pinToBottom = useCallback(() => {
    const list = listRef.current
    if (list && atBottom.current) list.scrollTop = list.scrollHeight
  }, [])

  useEffect(() => {
    atBottom.current = true
    pinToBottom()
  }, [chat.messages, open, pinToBottom])

  useEffect(() => {
    if (!open) return
    const list = listRef.current
    const onScroll = () => {
      if (!list) return
      atBottom.current =
        list.scrollTop >=
        list.scrollHeight - list.clientHeight - BOTTOM_SLACK_PX
    }
    list?.addEventListener('scroll', onScroll, { passive: true })
    const viewport = window.visualViewport
    viewport?.addEventListener('resize', pinToBottom)
    window.addEventListener('resize', pinToBottom)
    const observer =
      list && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => pinToBottom())
        : undefined
    if (list) observer?.observe(list)
    return () => {
      list?.removeEventListener('scroll', onScroll)
      viewport?.removeEventListener('resize', pinToBottom)
      window.removeEventListener('resize', pinToBottom)
      observer?.disconnect()
    }
  }, [open, pinToBottom])

  // The composer is disabled while Midas replies; hand focus back after.
  useEffect(() => {
    if (open && ready && !chat.replying) inputRef.current?.focus()
  }, [open, ready, chat.replying])

  // Announce finished replies once, not every streamed chunk.
  const announcement = useMemo(() => {
    const finished = [...chat.messages]
      .reverse()
      .find(
        m =>
          (m.author_type === 'midas' || m.author_type === 'agent') &&
          m.status === 'complete'
      )
    if (!finished) return ''
    const who = finished.author_type === 'agent' ? 'Goldie team' : 'Midas'
    return `${who}: ${finished.body}`
  }, [chat.messages])

  const submit = async (text: string) => {
    if (chat.replying || !ready || !text.trim()) return
    if (await chat.send(text)) setDraft('')
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit(draft)
    }
  }

  const onQuickReply = (reply: string) => {
    if (reply === TALK_TO_A_PERSON) chat.handover()
    else submit(reply)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby={TITLE_ID}
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(33, 30, 22, 0.12)' } }
      }}
      PaperProps={{
        sx: fullScreen
          ? { display: 'flex', flexDirection: 'column' }
          : {
              position: 'fixed',
              right: 16,
              bottom: 16,
              m: 0,
              width: 400,
              maxWidth: 'calc(100vw - 32px)',
              height: 'min(640px, calc(100vh - 32px))',
              maxHeight: 'none',
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column'
            }
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        gap={1.5}
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid #EEE9E0' }}
      >
        <Box flex={1} minWidth={0}>
          <Typography id={TITLE_ID} component='h2' fontWeight={700}>
            {LAUNCHER_LABEL}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Goldie&apos;s AI assistant
          </Typography>
        </Box>
        <IconButton aria-label='Close chat' onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {chat.handedOver && (
        <Alert severity='info' icon={false} sx={{ borderRadius: 0 }}>
          <Box component='span' display='block'>
            {HANDED_OVER_BANNER}
          </Box>
          {config.support_hours && (
            <Box component='span' display='block' mt={0.5}>
              {`Support hours: ${config.support_hours}`}
            </Box>
          )}
        </Alert>
      )}

      <Stack
        ref={listRef}
        role='log'
        aria-live='off'
        aria-label='Conversation with Midas'
        gap={1.5}
        sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}
      >
        {/* The welcome text is the backend's (GET /midas/config), as set in admin. */}
        <IncomingBubble label='Midas'>
          <Box sx={incomingSx}>
            <MarkdownLite text={config.welcome_message} />
          </Box>
        </IncomingBubble>

        {ready && !hasCustomerMessage && !chat.handedOver && (
          <Stack direction='row' flexWrap='wrap' gap={1}>
            {config.quick_replies.map(reply => (
              <Chip
                key={reply}
                label={reply}
                variant='outlined'
                color='primary'
                onClick={() => onQuickReply(reply)}
                disabled={chat.replying}
              />
            ))}
          </Stack>
        )}

        {chat.messages.map(message => (
          <MessageBubble
            key={message.id}
            message={message}
            onFeedback={chat.giveFeedback}
            onRetry={
              message === lastMessage && chat.lastCustomerBody
                ? () => submit(chat.lastCustomerBody ?? '')
                : undefined
            }
          />
        ))}

        {chat.loading && (
          <Typography variant='caption' color='text.secondary'>
            Connecting…
          </Typography>
        )}

        {chat.error && (
          <Alert
            severity='error'
            action={
              <Button
                variant='text'
                size='small'
                onClick={chat.retry}
                sx={textButtonSx}
              >
                Retry
              </Button>
            }
          >
            Midas couldn&apos;t connect just now.
          </Alert>
        )}

        {chat.closed && (
          <Alert
            severity='info'
            action={
              <Button
                variant='text'
                size='small'
                onClick={chat.startNewChat}
                sx={textButtonSx}
              >
                Start a new chat
              </Button>
            }
          >
            This conversation is closed.
          </Alert>
        )}

        {chat.sendError && (
          <Alert severity='error' role='alert'>
            {chat.sendError}
          </Alert>
        )}
      </Stack>

      <Box
        aria-live='polite'
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)'
        }}
      >
        {announcement}
      </Box>

      <Box sx={{ borderTop: '1px solid #EEE9E0', px: 2, pt: 1, pb: 1.5 }}>
        {ready && !chat.handedOver && showHandoverAction && (
          <Button
            variant='text'
            size='small'
            onClick={chat.handover}
            disabled={chat.replying}
            sx={{ ...textButtonSx, mb: 0.5, px: 0.5 }}
          >
            {TALK_TO_A_PERSON}
          </Button>
        )}
        <Stack direction='row' alignItems='flex-end' gap={1}>
          <InputBase
            inputRef={inputRef}
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              chat.handedOver ? 'Message the Goldie team' : COMPOSER_PLACEHOLDER
            }
            inputProps={{ 'aria-label': 'Message' }}
            disabled={!ready || chat.replying}
            multiline
            maxRows={5}
            sx={{
              flex: 1,
              bgcolor: '#F7F5F0',
              borderRadius: 5,
              px: 2,
              py: 1,
              fontSize: '0.9375rem'
            }}
          />
          <IconButton
            aria-label='Send'
            color='primary'
            onClick={() => submit(draft)}
            disabled={!ready || chat.replying || !draft.trim()}
          >
            <SendRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    </Dialog>
  )
}
